import React, { useRef, useState, useCallback } from "react";
import { withJsonFormsControlProps } from "@jsonforms/react";
import SFButton from "../../button";
import { rankWith, schemaMatches } from "@jsonforms/core";
import { SFImageIcon, SFUploadIcon } from "../../icons";
import SFTextField from "../../textField";
import SFAvatar from "../../avatar";
import getStyled from "../../../hooks/getStyled";
import { SFStack } from "../../grid";
import { useTranslation } from "react-i18next";
import SFTypography from "../../typography";
import SFDialog from "../../dialog";
import SFProgressBar from "../../progressBar";
import { uploadAsset } from "../../../services/assetUploadService";
import { useDispatch } from "react-redux";
import { toastActions } from "@base-provider";
import InputErrorLabel from "../../inputErrorLabel";

const { styled } = getStyled();

const SFAvatarStyled = styled(SFAvatar)(({ theme }) => ({
  backgroundColor: theme.palette.grey50.main,
  border: `1px ${theme.palette.grey250.main} solid`,
  borderRadius: "4px",
  color: theme.palette.grey450.main,
  marginRight: "8px",
}));

const StyledSFStack = styled(SFStack)(({ theme }) => ({
  width: "100%",
}));

const ButtonStack = styled(SFStack)(({ theme }) => ({
  marginLeft: theme.spacing(3),
}));

const AssetUploadButtonRenderer = ({
  data,
  handleChange,
  path,
  uischema,
  errors,
  onChange,
}) => {
  const { t } = useTranslation();
  const { i18nKey, label, options } = uischema;
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [fileContent, setFileContent] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFailed, setUploadFailed] = useState(false);
  const dispatch = useDispatch();

  const validateFile = (file) => {
    const fileType = options?.fileType;
    const maxFileSize = options?.maxFileSize * 1024 * 1024 || 10 * 1024 * 1024; // 10MB default

    const validFileTypes = {
      image: ["image/jpeg", "image/jpg"], // Support both "image/jpeg" and "image/jpg"
      video: ["video/mp4"],
      subtitle: ["text/vtt"],
      pdf: ["application/pdf"],
    };

    if (!validFileTypes[fileType]?.includes(file.type)) {
      return t("components.formBuilder.errors.invalidFileType");
    }

    if (file.size > maxFileSize) {
      return t("components.formBuilder.errors.fileTooLarge");
    }
  };

  const handleDefaultFileName = () => {
    switch (options?.fileType) {
    case "image":
      return t("components.formBuilder.defaultAssetNames.image");
    case "video":
      return t("components.formBuilder.defaultAssetNames.video");
    case "subtitle":
      return t("components.formBuilder.defaultAssetNames.subtitles");
    case "pdf":
      return t("components.formBuilder.defaultAssetNames.pdf");
    default:
      return "";
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileUpload = async (file) => {
    setOpenDialog(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append("File", file);

    await uploadAsset(formData, options?.skillPath)
      .then(() => {
        setUploadProgress(100);
        dispatch(toastActions.showSuccessMessage(t("fileUploadScreen.successMessage")));
      })
      .catch(() => {
        setErrorMessage(t("components.formBuilder.errors.uploadFailed"));
        setUploadFailed(true);
      });
  };

  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files[0];

      if (file) {
        setErrorMessage("");
        const validationError = validateFile(file);
        if (validationError) {
          setErrorMessage(validationError);
          setOpenDialog(true);
          setUploadFailed(true);
          event.target.value = "";
          return;
        }
        setFileName(file.name);
        setFileContent(file);
        handleChange(path, file.name);
        handleFileUpload(file);
        event.target.value = "";
      }
    },
    [handleChange, path]
  );

  return (
    <SFStack direction="column">
      <SFDialog
        title={t("components.formBuilder.uploadDialog.title")}
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setErrorMessage("");
          setUploadFailed(false);
        }}
      >
        <SFProgressBar
          loadingText={t("components.formBuilder.uploadDialog.importProgress")}
          variant={"determinate"}
          progress={uploadProgress}
          failed={uploadFailed}
        />
        {uploadFailed && (
          <SFStack
            direction="row"
            justifyContent="space-between"
            spacing={1}
            marginTop={2}
          >
            <SFStack
              spacing={2.5}
            >
              <InputErrorLabel label={errorMessage} />
              <SFButton variant="secondary" onClick={() => {
                setOpenDialog(false);
                setErrorMessage("");
                setUploadFailed(false);
              }}>
                {t("components.formBuilder.buttonLabels.cancelUpload")}
              </SFButton>
            </SFStack>
          </SFStack>
        )}
      </SFDialog>
      <SFStack direction="row" alignItems="center">
        <SFAvatarStyled variant="square" icon={<SFImageIcon />} />
        <StyledSFStack>
          <SFTextField
            label={i18nKey ? t(i18nKey) : label}
            value={data || fileName || handleDefaultFileName()}
            variant="filled"
            readOnly
            required
            onChange={onChange}
            errors={errorMessage ? [errorMessage] : errors}
            maxLength={options?.maxLength}
            disabled
          />
          {options?.helperText && (
            <SFTypography variant="tableList" align="left">
              {t(options?.helperText)}
            </SFTypography>
          )}
        </StyledSFStack>
        <ButtonStack>
          {!options.hideUploadButton && <SFButton variant={options.variant} onClick={handleButtonClick}>
            <SFUploadIcon />
            {t("components.formBuilder.buttonLabels.upload")}
          </SFButton>}
        </ButtonStack>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <input type="hidden" name={`${path}.fileContent`} value={fileContent} />
      </SFStack>
    </SFStack>
  );
};

export const AssetUploadButtonTester = rankWith(
  106,
  schemaMatches((schema) => schema.assetUpload)
);

export default withJsonFormsControlProps(AssetUploadButtonRenderer);
